import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import { organizationService } from '@/services/api/organization.service';
import { sessionsService, type NoteUpdateInput } from '@/services/api/sessions.service';
import type { NoteDocument } from '@/types/sessions';

export const THERAPY_BUSINESS_TYPE = 'psychotherapy';

export const sessionKeys = {
  overview: () => ['sessions-overview'] as const,
  note: (id: string) => ['session-note', id] as const,
  client: (customerId: string | number) => ['session-client', String(customerId)] as const,
  accessLog: (customerId: string | number) => ['session-access-log', String(customerId)] as const,
};

/** Pure check, kept separate so it can be unit-tested. */
export function isTherapyPractice(businessType: string | null | undefined, hasProviderProfile: boolean | undefined): boolean {
  return businessType === THERAPY_BUSINESS_TYPE && Boolean(hasProviderProfile);
}

/**
 * Sloty Sessions is only for providers in a psychotherapy practice. Uses the
 * same ['organization'] query as the admin layout, so it adds no request.
 */
export function useIsTherapyPractice() {
  const { user } = useAuth();
  const { data: organization, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
    enabled: Boolean(user) && user?.role !== 'customer',
  });
  return {
    enabled: isTherapyPractice(organization?.business_type, user?.hasProviderProfile),
    isLoading,
  };
}

export function useSessionsOverview() {
  return useQuery({
    queryKey: sessionKeys.overview(),
    queryFn: () => sessionsService.getOverview(),
  });
}

export function useSessionNote(id: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.note(id ?? ''),
    queryFn: () => sessionsService.getNote(id!),
    enabled: Boolean(id),
    // The editor owns the document while it's open; never refetch under it.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useClientRecord(customerId: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.client(customerId ?? ''),
    queryFn: () => sessionsService.getClientRecord(customerId!),
    enabled: Boolean(customerId),
  });
}

export function useAccessLog(customerId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: sessionKeys.accessLog(customerId ?? ''),
    queryFn: () => sessionsService.getAccessLog(customerId!),
    enabled: Boolean(customerId) && enabled,
  });
}

function useErrorToast() {
  const { toast } = useToast();
  const { t } = useTranslation('sessions');
  return (error: unknown) =>
    toast({
      title: t('toasts.errorTitle'),
      description: sanitizeErrorMessage(error, t('toasts.errorDescription')),
      variant: 'destructive',
    });
}

export function useOpenNoteForBooking() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ bookingId, content }: { bookingId: string | number; content?: NoteDocument }) =>
      sessionsService.openNoteForBooking(bookingId, content),
    onSuccess: (note) => {
      queryClient.setQueryData(sessionKeys.note(note.id), note);
      queryClient.invalidateQueries({ queryKey: ['sessions-overview'] });
      // The customer page shows each booking's note status.
      queryClient.invalidateQueries({ queryKey: ['customer'] });
    },
    onError,
  });
}

/** Autosave. Does not refetch the note (the editor holds the latest content). */
export function useUpdateNote(id: string) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: NoteUpdateInput) => sessionsService.updateNote(id, input),
    onSuccess: (note, input) => {
      if (input.status) {
        queryClient.setQueryData(sessionKeys.note(id), note);
        queryClient.invalidateQueries({ queryKey: ['sessions-overview'] });
      }
      if (note.customer) queryClient.invalidateQueries({ queryKey: sessionKeys.client(note.customer.publicId) });
    },
    onError,
  });
}

export function useSetMarkResolved(noteId?: string) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ markId, resolved }: { markId: number; resolved: boolean }) => sessionsService.setMarkResolved(markId, resolved),
    onSuccess: () => {
      if (noteId) queryClient.invalidateQueries({ queryKey: sessionKeys.note(noteId) });
      queryClient.invalidateQueries({ queryKey: ['session-client'] });
    },
    onError,
  });
}

export function usePinMutations(customerId: string) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: sessionKeys.client(customerId) });
    queryClient.invalidateQueries({ queryKey: ['session-note'] });
  };
  return {
    create: useMutation({ mutationFn: (text: string) => sessionsService.createPin(customerId, text), onSuccess: invalidate, onError }),
    update: useMutation({
      mutationFn: ({ pinId, text }: { pinId: string; text: string }) => sessionsService.updatePin(customerId, pinId, text),
      onSuccess: invalidate,
      onError,
    }),
    remove: useMutation({ mutationFn: (pinId: string) => sessionsService.deletePin(customerId, pinId), onSuccess: invalidate, onError }),
  };
}
